import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell, LabelList } from 'recharts';
import { ChevronLeft, ChevronRight, ArrowDown, ArrowUp } from 'lucide-react';
import { Button, ChartContainer, ChartTooltip, ChartTooltipContent, Skeleton, useIsMobile, formatCurrency } from '@hotelise/ui';

import {
	getDashboardKPIData,
	getDashboardKPILoading,
	getKPISortBy,
	getKPICurrentPage,
	getDashboardKPIReport,
	setKPISortBy,
	setKPICurrentPage,
} from 'state/modules/dashboard';
import { PropertyKPIData } from 'interfaces/reports';

interface KPIDashboardProps {
	kpiMetric?: 'income' | 'payouts' | 'commissions' | 'expenses' | 'cleaning' | 'adr' | 'revPar' | 'occupancy' | 'alos' | 'ownerBlockedDays';
}

const METRIC_LABELS: Record<string, string> = {
	income: 'Income',
	payouts: 'Net Income',
	commissions: 'Commissions',
	expenses: 'Expenses',
	cleaning: 'Cleaning',
	adr: 'ADR',
	revPar: 'RevPAR',
	occupancy: 'Occupancy Rate',
	alos: 'ALOS',
	ownerBlockedDays: 'OOO',
};

interface BarLabelProps {
	x?: number;
	y?: number;
	width?: number;
	value?: number | string;
	isMobile: boolean;
	format: (v: number) => string;
	fontSize: number;
}

const BarLabel = ({ x, y, width, value, isMobile, format, fontSize }: BarLabelProps) => {
	const labelX = (x ?? 0) + (width ?? 0) / 2;
	const labelY = (y ?? 0) - (isMobile ? 4 : 6);
	const num = typeof value === 'number' ? value : Number(value);
	const text = format(Number.isNaN(num) ? 0 : num);

	return (
		<text
			x={labelX}
			y={labelY}
			textAnchor="middle"
			fill="hsl(var(--muted-foreground))"
			fontSize={fontSize}
		>
			{text}
		</text>
	);
};

const ITEMS_PER_PAGE = 10;
const SKEL_BARS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j'];

const chartConfig = Object.fromEntries(
	Object.entries(METRIC_LABELS).map(([key, label]) => [
		key,
		{ label, color: 'hsl(var(--primary))' },
	])
);

const KPIDashboard = ({ kpiMetric = 'income' }: KPIDashboardProps): JSX.Element => {
	const dispatch = useDispatch();
	const isMobile = useIsMobile();
	const kpiData = useSelector(getDashboardKPIData);
	const isLoading = useSelector(getDashboardKPILoading);
	const sortBy = useSelector(getKPISortBy);
	const currentPage = useSelector(getKPICurrentPage);

	useEffect(() => {
		dispatch(getDashboardKPIReport());
	}, [dispatch]);

	const sortedData = useMemo(() => {
		if (!kpiData || kpiData.length === 0) return [];
		const sorted = [...kpiData];
		sorted.sort((a, b) => {
			const aVal = a[kpiMetric as keyof PropertyKPIData] as number;
			const bVal = b[kpiMetric as keyof PropertyKPIData] as number;
			return sortBy === 'highest' ? bVal - aVal : aVal - bVal;
		});
		return sorted;
	}, [kpiData, sortBy, kpiMetric]);

	const totalPages = Math.max(1, Math.ceil(sortedData.length / ITEMS_PER_PAGE));
	const displayedData = sortedData.slice(
		(currentPage - 1) * ITEMS_PER_PAGE,
		currentPage * ITEMS_PER_PAGE
	);

	const handleSortToggle = () => {
		dispatch(setKPISortBy(sortBy === 'highest' ? 'lowest' : 'highest'));
		dispatch(setKPICurrentPage(1));
	};

	const handlePageChange = (newPage: number) => {
		if (newPage >= 1 && newPage <= totalPages) {
			dispatch(setKPICurrentPage(newPage));
		}
	};

	const formatValue = (value: number) => {
		if (kpiMetric === 'occupancy') return `${value.toFixed(1)}%`;
		if (kpiMetric === 'alos') return `${value.toFixed(1)}d`;
		if (kpiMetric === 'ownerBlockedDays') return `${Math.round(value)}`;
		return formatCurrency(value);
	};

	const formatValueShort = (value: number) => {
		if (kpiMetric === 'occupancy') return `${Math.round(value)}%`;
		if (kpiMetric === 'alos') return `${value.toFixed(1)}d`;
		if (kpiMetric === 'ownerBlockedDays') return `${Math.round(value)}`;
		return formatCurrency(value, isMobile);
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const renderBarLabel = useCallback((props: any) => (
		<BarLabel
			{...props}
			isMobile={isMobile}
			format={formatValueShort}
			fontSize={isMobile ? 8 : 10}
		/>
	), [isMobile, kpiMetric]);

	const chartData = displayedData.map((p) => ({
		...p,
		name: isMobile && p.propertyName.length > 10
			? `${p.propertyName.substring(0, 10)}...`
			: p.propertyName,
	}));

	if (isLoading) {
		return (
			<div className="bg-card rounded-lg p-6 border">
				<div className="flex items-center justify-between mb-6">
					<Skeleton className="h-6 w-48" />
					<div className="flex items-center gap-2">
						<Skeleton className="h-9 w-32 rounded-md" />
					</div>
				</div>
				<div className={`${isMobile ? 'h-[300px]' : 'h-[400px]'} flex items-end gap-3 pt-8 pb-16 px-4`}>
					{SKEL_BARS.map((id) => (
						<div key={id} className="flex-1 flex flex-col items-center gap-2">
							<Skeleton
								className="w-full rounded-t-md"
								style={{ height: `${Math.random() * 60 + 20}%` }}
							/>
							<Skeleton className="h-3 w-10" />
						</div>
					))}
				</div>
			</div>
		);
	}

	if (!kpiData || kpiData.length === 0) {
		return (
			<div className="bg-card rounded-lg p-6 border">
				<p className="text-center py-10 text-muted-foreground">
					No KPI data available for the selected period
				</p>
			</div>
		);
	}

	return (
		<div className="bg-card rounded-lg p-6 border">
			{/* Header */}
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
				<h2 className="text-lg font-semibold">{METRIC_LABELS[kpiMetric]} by Property</h2>

				<div className="flex items-center gap-2">
					{/* Sort Toggle */}
					<Button
						variant="outline"
						size="sm"
						onClick={handleSortToggle}
						className="gap-2"
					>
						{sortBy === 'highest' ? (
							<ArrowDown className="h-4 w-4" />
						) : (
							<ArrowUp className="h-4 w-4" />
						)}
						{sortBy === 'highest' ? 'Highest First' : 'Lowest First'}
					</Button>

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="flex items-center gap-1">
							<Button
								variant="outline"
								size="icon"
								onClick={() => handlePageChange(currentPage - 1)}
								disabled={currentPage === 1}
								className="h-9 w-9"
							>
								<ChevronLeft className="h-4 w-4" />
							</Button>
							<span className="text-sm text-muted-foreground min-w-[60px] text-center">
								{currentPage} / {totalPages}
							</span>
							<Button
								variant="outline"
								size="icon"
								onClick={() => handlePageChange(currentPage + 1)}
								disabled={currentPage >= totalPages}
								className="h-9 w-9"
							>
								<ChevronRight className="h-4 w-4" />
							</Button>
						</div>
					)}
				</div>
			</div>

			{/* Chart */}
			<ChartContainer config={chartConfig} className={`${isMobile ? 'h-[300px]' : 'h-[400px]'} w-full [&>div]:!aspect-auto`}>
				<BarChart
					data={chartData}
					margin={{
						top: isMobile ? 25 : 40,
						right: isMobile ? 10 : 30,
						left: isMobile ? 10 : 20,
						bottom: isMobile ? 80 : 60,
					}}
				>
					<CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
					<XAxis
						dataKey="name"
						className="text-xs"
						tick={{ fontSize: isMobile ? 9 : 11 }}
						angle={-45}
						textAnchor="end"
						height={isMobile ? 80 : 60}
						interval={0}
					/>
					<YAxis
						className="text-xs"
						tick={{ fontSize: isMobile ? 9 : 12 }}
						tickFormatter={(value) => formatValueShort(value)}
						width={isMobile ? 40 : 55}
					/>
					<ChartTooltip
						content={<ChartTooltipContent />}
						formatter={(value: number) => [formatValue(value), METRIC_LABELS[kpiMetric]]}
					/>
					<Bar dataKey={kpiMetric} radius={[4, 4, 0, 0]}>
						<LabelList
							dataKey={kpiMetric}
							content={renderBarLabel}
						/>
						{chartData.map((entry) => (
							<Cell key={entry.propertyId} fill="hsl(var(--primary))" />
						))}
					</Bar>
				</BarChart>
			</ChartContainer>
		</div>
	);
};

export default KPIDashboard;
